import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
// Use the configured API instance instead of direct axios import
import { api, getApiUrl } from '../utils/apiConfig';
import { getUserData } from '../utils/userUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartBar, faMessage, faFile, faUpload, faDownload, faCheckCircle, faClock, faUsers, faClipboardList, faRocket, faPen, faTachometerAlt, faRobot, faSearch, faBolt, faBullseye } from '@fortawesome/free-solid-svg-icons';
import withAccountSelection from '../hocs/withAccountSelection';
import { useAccount } from '../contexts/AccountContext';
import { useAccountSettings } from '../hooks/useAccountSettings';
import './StatusPage.css';

/**
 * How an activity row was triggered. The API returns three values; only
 * 'manual' means a person clicked something.
 *
 * 'cron'        the daily performance-analysis scheduler
 * 'auto-pacing' an auto-pacing schedule's optimize step
 *
 * Both machine kinds come back with no userName/userEmail — the scheduler
 * borrows an account admin to satisfy the route, and a paced run belongs to a
 * schedule rather than to whoever created it — so neither is named here.
 */
const TRIGGER_LABEL = { cron: 'Cron', 'auto-pacing': 'Auto-pacing', manual: 'Manual' };
const isMachineRun = (r) => r.triggeredBy === 'cron' || r.triggeredBy === 'auto-pacing';

function StatusPage({ isReady, user }) {
  const { getAccountIdToUse } = useAccount();
  const [stats, setStats] = useState(null);
  const [v2Stats, setV2Stats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Orchestration status state
  const [orchestrations, setOrchestrations] = useState([]);
  const [orchestrationSummary, setOrchestrationSummary] = useState({});
  const [orchestrationLoading, setOrchestrationLoading] = useState(true);
  const [orchestrationError, setOrchestrationError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Tab state (for V2 users)
  const [activeTab, setActiveTab] = useState('analytics');

  // Activity report state
  const [activityPeriodType, setActivityPeriodType] = useState('month');
  const [activityPeriodValue, setActivityPeriodValue] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [activityReport, setActivityReport] = useState(null);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityError, setActivityError] = useState(null);
  const isFetchingActivityRef = useRef(false);

  // Account settings for download gating (comma-separated email list)
  const accountId = getAccountIdToUse();
  const { settings: accountSettings } = useAccountSettings(accountId, user);
  const canDownloadReport = useMemo(() => {
    const settingValue = accountSettings?.['activity-report-download'];
    if (!settingValue) return false;
    const userEmail = (user?.email || getUserData()?.email || '').toLowerCase().trim();
    if (!userEmail) return false;
    const allowedEmails = settingValue.split(',').map(e => e.trim().toLowerCase()).filter(e => e.length > 0);
    return allowedEmails.includes(userEmail);
  }, [accountSettings, user]);

  // Add a ref to track if we're currently fetching stats
  const isFetchingRef = useRef(false);
  const isFetchingOrchestrationRef = useRef(false);

  // Detect if user is on v2 (new experience)
  const isV2User = useMemo(() => {
    const saved = localStorage.getItem('useNewExperience');
    return saved === 'true';
  }, []);

  // Memoize the fetchStats function to prevent unnecessary re-creation
  const fetchStats = useCallback(async () => {
    // If we're already fetching stats, don't start another fetch
    if (isFetchingRef.current) {
      return;
    }

    // If the component is not ready yet, don't fetch stats
    if (!isReady) {
      return;
    }

    try {
      // Set the fetching flag to prevent duplicate API calls
      isFetchingRef.current = true;
      setIsLoading(true);

      const userData = getUserData();
      const userId = userData?.userId;

      if (!userId) {
        setError('No valid user session found');
        setIsLoading(false);
        isFetchingRef.current = false;
        return;
      }

      // Use the getAccountIdToUse function from AccountContext
      // This ensures we use the same account ID across all components
      const accountIdToUse = getAccountIdToUse();

      // If no account ID is available, show an error
      if (!accountIdToUse) {
        setError('No account selected or available');
        setIsLoading(false);
        isFetchingRef.current = false;
        return;
      }

      // Call different endpoints based on v1/v2
      if (isV2User) {
        const response = await api.get(getApiUrl('/api/v2/stats'), {
          params: { userId, accountId: accountIdToUse }
        });
        setV2Stats(response.data);
      } else {
        const response = await api.get(getApiUrl('/api/stats'), {
          params: { userId, accountId: accountIdToUse }
        });
        setStats(response.data);
      }
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching stats:', error);
      setError('Failed to load statistics. Please try again later.');
      setIsLoading(false);
    } finally {
      // Reset the fetching flag
      isFetchingRef.current = false;
    }
  }, [isReady, getAccountIdToUse, isV2User]);

  // Helper function to format timestamps to local time
  const formatLocalTime = useCallback((utcTimestamp) => {
    if (!utcTimestamp) return 'N/A';

    try {
      const date = new Date(utcTimestamp);
      return date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });
    } catch (error) {
      console.error('Error formatting timestamp:', error);
      return 'Invalid Date';
    }
  }, []);

  // Helper function to format relative time (e.g., "5 minutes ago")
  const formatRelativeTime = useCallback((utcTimestamp) => {
    if (!utcTimestamp) return 'N/A';

    try {
      const date = new Date(utcTimestamp);
      const now = new Date();
      const diffMs = now - date;
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      const diffHours = Math.floor(diffMinutes / 60);
      const diffDays = Math.floor(diffHours / 24);

      if (diffMinutes < 1) return 'Just now';
      if (diffMinutes < 60) return `${diffMinutes}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;

      // For older dates, show the actual date
      return date.toLocaleDateString();
    } catch (error) {
      console.error('Error formatting relative time:', error);
      return 'Invalid Date';
    }
  }, []);

  // Get user's timezone for display
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Fetch orchestration status
  const fetchOrchestrationStatus = useCallback(async () => {
    if (isFetchingOrchestrationRef.current || !isReady) {
      return;
    }

    try {
      isFetchingOrchestrationRef.current = true;
      setOrchestrationLoading(true);

      const userData = getUserData();
      const userId = userData?.userId;

      if (!userId) {
        setOrchestrationError('No valid user session found');
        setOrchestrationLoading(false);
        isFetchingOrchestrationRef.current = false;
        return;
      }

      // Use the getAccountIdToUse function from AccountContext
      const accountIdToUse = getAccountIdToUse();

      if (!accountIdToUse) {
        setOrchestrationError('No account selected or available');
        setOrchestrationLoading(false);
        isFetchingOrchestrationRef.current = false;
        return;
      }

      const response = await api.get(getApiUrl('/api/orchestration/status'), {
        params: {
          userId,
          accountId: accountIdToUse,
          limit: 20,
          time_range: '24h'
        }
      });

      setOrchestrations(response.data.orchestrations);
      setOrchestrationSummary(response.data.summary);
      setOrchestrationError(null);
    } catch (error) {
      console.error('Error fetching orchestration status:', error);
      setOrchestrationError('Failed to load orchestration status');
    } finally {
      setOrchestrationLoading(false);
      isFetchingOrchestrationRef.current = false;
    }
  }, [isReady, getAccountIdToUse]);

  // Force a re-fetch when selectedAccountId changes, user changes, or when component mounts
  useEffect(() => {
    // If the component is not ready yet, don't fetch stats
    if (!isReady) {
      return;
    }

    // Use the getAccountIdToUse function from AccountContext
    // This ensures we use the same account ID across all components
    const accountIdToUse = getAccountIdToUse();

    // If no account ID is available, don't fetch stats
    if (!accountIdToUse) {
      setError('No account selected or available');
      setIsLoading(false);
      return;
    }

    // Reset error state when refetching
    setError(null);

    fetchStats();
  }, [fetchStats, isReady, getAccountIdToUse]);

  // Fetch orchestration status on mount and set up auto-refresh
  useEffect(() => {
    if (!isReady) return;

    fetchOrchestrationStatus();

    let interval;
    if (autoRefresh) {
      interval = setInterval(() => {
        fetchOrchestrationStatus();
      }, 10000); // Refresh every 10 seconds
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [fetchOrchestrationStatus, autoRefresh, isReady]);

  // Fetch activity report data
  const fetchActivityReport = useCallback(async () => {
    if (isFetchingActivityRef.current || !isReady) return;

    try {
      isFetchingActivityRef.current = true;
      setActivityLoading(true);
      setActivityError(null);

      const userData = getUserData();
      const userId = userData?.userId;
      const accountIdToUse = getAccountIdToUse();

      if (!userId || !accountIdToUse) {
        setActivityError('No valid session or account');
        setActivityLoading(false);
        isFetchingActivityRef.current = false;
        return;
      }

      const response = await api.get(getApiUrl('/api/v2/stats/activity-report'), {
        params: {
          userId,
          accountId: accountIdToUse,
          periodType: activityPeriodType,
          periodValue: activityPeriodValue
        }
      });

      setActivityReport(response.data);
    } catch (err) {
      console.error('Error fetching activity report:', err);
      setActivityError('Failed to load activity report');
    } finally {
      setActivityLoading(false);
      isFetchingActivityRef.current = false;
    }
  }, [isReady, getAccountIdToUse, activityPeriodType, activityPeriodValue]);

  // Fetch activity report when tab is active and period changes
  useEffect(() => {
    if (activeTab === 'activity-report' && isReady && isV2User) {
      fetchActivityReport();
    }
  }, [activeTab, fetchActivityReport, isReady, isV2User]);

  // Excel download handler - each metric section is a separate sheet/tab
  const handleDownloadExcel = useCallback(() => {
    if (!activityReport?.metrics) return;

    const XLSX = require('xlsx');
    const { metrics } = activityReport;
    const periodLabel = activityReport.period?.value || activityPeriodValue;

    const wb = XLSX.utils.book_new();

    // Summary sheet
    const summaryData = [
      ['Activity Report Summary'],
      ['Period', periodLabel],
      [],
      ['Metric', 'Count'],
      ['Campaign Launches/Setups', metrics.campaignLaunches?.count ?? 0],
      ['Campaign Edits/Updates', metrics.campaignEdits?.count ?? 0],
      ['Manual Pacings', metrics.manualPacings?.count ?? 0],
      ['Auto Pacings', metrics.autoPacings?.count ?? 0],
      ['Diagnostic Analyses', metrics.diagnosticAnalyses?.count ?? 0],
      ['Optimizations', metrics.optimizations?.count ?? 0],
      ['Upload Creatives', metrics.uploadCreatives?.count ?? 0],
      ['Assign Creatives', metrics.assignCreatives?.count ?? 0],
      ['Import Campaigns', metrics.importCampaigns?.count ?? 0],
      ['Unique Campaigns Managed', metrics.uniqueCampaignsManaged?.count ?? 0],
    ];
    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(wb, summarySheet, 'Summary');

    // Helper to add a detail sheet
    const addSheet = (sheetName, details, columns, rowMapper) => {
      if (!details || details.length === 0) return;
      const data = [columns, ...details.map(r => rowMapper(r).map(v => v ?? ''))];
      const ws = XLSX.utils.aoa_to_sheet(data);
      // Auto-size columns
      ws['!cols'] = columns.map((_, i) => ({
        wch: Math.max(columns[i].length, ...data.slice(1).map(row => String(row[i] ?? '').length)) + 2
      }));
      XLSX.utils.book_append_sheet(wb, ws, sheetName.substring(0, 31)); // Excel 31 char tab name limit
    };

    addSheet('Unique Campaigns', metrics.uniqueCampaignsManaged?.details,
      ['Campaign', 'Platform', 'Status', 'Last Updated'],
      r => [r.campaignName, r.platform, r.status, r.updatedAt || r.createdAt]
    );

    addSheet('Launches', metrics.campaignLaunches?.details,
      ['Platform', 'Status', 'User', 'Email', 'Date'],
      r => [r.platform, r.status, r.userName, r.userEmail, r.createdAt]
    );

    addSheet('Edits', metrics.campaignEdits?.details,
      ['Campaign', 'Platform', 'Status', 'User', 'Email', 'Date'],
      r => [r.campaignName, r.platform, r.status, r.userName, r.userEmail, r.createdAt]
    );

    addSheet('Manual Pacings', metrics.manualPacings?.details,
      ['Name', 'Platform', 'Status', 'Total Campaigns', 'Successful', 'Failed', 'User', 'Email', 'Date'],
      r => [r.name, r.platform, r.status, r.totalCampaigns, r.successfulCampaigns, r.failedCampaigns, r.userName, r.userEmail, r.createdAt]
    );

    addSheet('Auto Pacings', metrics.autoPacings?.details,
      ['Name', 'Type', 'Platform', 'Status', 'User', 'Email', 'Date'],
      r => [r.name, r.type, r.platform, r.status, r.userName, r.userEmail, r.createdAt]
    );

    addSheet('Diagnostic Analyses', metrics.diagnosticAnalyses?.details,
      ['Campaign', 'Platform', 'Status', 'Trigger', 'User', 'Email', 'Date'],
      // Cron runs are recorded under a borrowed account admin; don't name them.
      r => [r.campaignName, r.platform, r.status, TRIGGER_LABEL[r.triggeredBy] || 'Manual', isMachineRun(r) ? 'Scheduler' : r.userName, isMachineRun(r) ? '' : r.userEmail, r.createdAt]
    );

    addSheet('Optimizations', metrics.optimizations?.details,
      ['Campaign', 'Platform', 'Status', 'Trigger', 'User', 'Email', 'Date'],
      // Optimizations is a superset of Diagnostic Analyses and carries the same
      // machine-run rows; the API leaves those unattributed rather than naming
      // the admin the scheduler borrowed or the owner of a pacing schedule.
      r => [r.campaignName, r.platform, r.status, TRIGGER_LABEL[r.triggeredBy] || 'Manual', isMachineRun(r) ? 'Scheduler' : r.userName, isMachineRun(r) ? '' : r.userEmail, r.createdAt]
    );

    addSheet('Upload Creatives', metrics.uploadCreatives?.details,
      ['Campaign', 'Platform', 'Status', 'User', 'Email', 'Date'],
      r => [r.campaignName, r.platform, r.status, r.userName, r.userEmail, r.createdAt]
    );

    addSheet('Assign Creatives', metrics.assignCreatives?.details,
      ['Campaign', 'Platform', 'Status', 'User', 'Email', 'Date'],
      r => [r.campaignName, r.platform, r.status, r.userName, r.userEmail, r.createdAt]
    );

    addSheet('Import Campaigns', metrics.importCampaigns?.details,
      ['Campaign', 'Platform', 'Status', 'User', 'Email', 'Date'],
      r => [r.campaignName, r.platform, r.status, r.userName, r.userEmail, r.createdAt]
    );

    // Write and download
    const wbOut = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbOut], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `activity-report_${periodLabel}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [activityReport, activityPeriodValue]);

  // Handle period type toggle (reset value to current period)
  const handlePeriodTypeChange = useCallback((newType) => {
    setActivityPeriodType(newType);
    if (newType === 'month') {
      const now = new Date();
      setActivityPeriodValue(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
    } else {
      // Get current ISO week
      const now = new Date();
      const d = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
      d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
      const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
      const weekNo = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
      setActivityPeriodValue(`${d.getUTCFullYear()}-W${String(weekNo).padStart(2, '0')}`);
    }
  }, []);

  // Navigation is now handled by the Layout component

  // Helper function to render orchestration section (shared between v1 and v2)
  const renderOrchestrationSection = useCallback(() => {
    return (
      <div className="orchestration-section">
        <div className="section-header">
          <div className="section-title">
            <h2>Fast Track Orchestrations</h2>
            <small className="timezone-info">Times shown in {userTimezone}</small>
          </div>
          <div className="section-controls">
            <label className="auto-refresh-toggle">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
              />
              Auto-refresh (10s)
            </label>
            <button
              className="refresh-button"
              onClick={fetchOrchestrationStatus}
              disabled={orchestrationLoading}
            >
              {orchestrationLoading ? 'Loading...' : 'Refresh'}
            </button>
          </div>
        </div>

        {orchestrationError && (
          <div className="error-message">
            {orchestrationError}
          </div>
        )}

        {/* Orchestration Summary Cards */}
        <div className="orchestration-summary">
          <div className="summary-card active">
            <h4>Active</h4>
            <span className="summary-value">
              {orchestrationSummary.active_stats?.reduce((sum, stat) => sum + parseInt(stat.active_count || 0), 0) || 0}
            </span>
          </div>
          <div className="summary-card completed">
            <h4>Completed (24h)</h4>
            <span className="summary-value">
              {orchestrationSummary.by_type_and_status?.filter(s => s.status === 'completed').reduce((sum, stat) => sum + parseInt(stat.count || 0), 0) || 0}
            </span>
          </div>
          <div className="summary-card failed">
            <h4>Failed (24h)</h4>
            <span className="summary-value">
              {orchestrationSummary.by_type_and_status?.filter(s => s.status === 'failed').reduce((sum, stat) => sum + parseInt(stat.count || 0), 0) || 0}
            </span>
          </div>
          <div className="summary-card suspended">
            <h4>Suspended</h4>
            <span className="summary-value">
              {orchestrationSummary.active_stats?.reduce((sum, stat) => sum + parseInt(stat.suspended_count || 0), 0) || 0}
            </span>
          </div>
        </div>

        {/* Recent Orchestrations Table */}
        {orchestrations.length > 0 ? (
          <div className="orchestrations-table">
            <h3>Recent Orchestrations (Last 24h)</h3>
            <table className="activity-table">
              <thead>
                <tr>
                  <th>Message ID</th>
                  <th>Type</th>
                  <th>Platform</th>
                  <th>Status</th>
                  <th>Progress</th>
                  <th>Duration</th>
                  <th>Restarts</th>
                  <th>Created</th>
                  <th>Last Updated</th>
                </tr>
              </thead>
              <tbody>
                {orchestrations.slice(0, 10).map((orch) => (
                  <tr key={orch.message_id} className={orch.is_stuck ? 'stuck-row' : ''}>
                    <td>
                      <code className="message-id">{orch.message_id.substring(0, 8)}...</code>
                      {orch.is_stuck && <span className="stuck-indicator" title="Stuck for >2 hours">⚠️</span>}
                    </td>
                    <td>
                      <span className={`type-badge ${orch.workflow_type}`}>
                        {orch.workflow_type}
                      </span>
                      {orch.is_shell_workflow && <small className="shell-indicator">(shell)</small>}
                    </td>
                    <td>{orch.platform}</td>
                    <td>
                      <span className={`status-badge ${orch.status}`}>
                        {orch.status}
                      </span>
                      {orch.recovery_state !== 'active' && (
                        <small className="recovery-state">({orch.recovery_state})</small>
                      )}
                    </td>
                    <td>
                      <div className="progress-container">
                        <div className="progress-bar">
                          <div
                            className="progress-fill"
                            style={{width: `${orch.progress_percentage || 0}%`}}
                          ></div>
                        </div>
                        <span className="progress-text">{orch.progress_percentage || 0}%</span>
                      </div>
                      {orch.processing_steps.length > 0 && (
                        <div className="processing-steps">
                          {orch.processing_steps.map(step => (
                            <span key={step} className="processing-step">{step}</span>
                          ))}
                          {orch.has_parallel_processing && <span className="parallel-indicator" title="Parallel processing">⚡</span>}
                        </div>
                      )}
                    </td>
                    <td>{Math.round(orch.duration_minutes || 0)}m</td>
                    <td>
                      {orch.restart_count > 0 ? (
                        <span className="restart-count">{orch.restart_count}</span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td title={formatLocalTime(orch.created_at)}>
                      {formatRelativeTime(orch.created_at)}
                    </td>
                    <td title={formatLocalTime(orch.updated_at)}>
                      {formatRelativeTime(orch.updated_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {orchestrations.length > 10 && (
              <p className="table-note">Showing 10 most recent orchestrations out of {orchestrations.length} total</p>
            )}
          </div>
        ) : (
          !orchestrationLoading && (
            <div className="no-orchestrations">
              <p>No orchestrations found in the last 24 hours</p>
            </div>
          )
        )}
      </div>
    );
  }, [userTimezone, autoRefresh, orchestrationLoading, fetchOrchestrationStatus, orchestrationError, orchestrationSummary, orchestrations, formatLocalTime, formatRelativeTime]);

  // Expanded section state for activity report detail tables
  const [expandedMetric, setExpandedMetric] = useState(null);

  // Helper function to render activity report tab content
  const renderActivityReport = useCallback(() => {
    const metrics = activityReport?.metrics;

    const metricCards = [
      { key: 'uniqueCampaignsManaged', label: 'Unique Campaigns Managed', icon: faBullseye },
      { key: 'campaignLaunches', label: 'Campaign Launches/Setups', icon: faRocket },
      { key: 'campaignEdits', label: 'Campaign Edits/Updates', icon: faPen },
      { key: 'manualPacings', label: 'Manual Pacings', icon: faTachometerAlt },
      { key: 'autoPacings', label: 'Auto Pacings', icon: faRobot },
      { key: 'diagnosticAnalyses', label: 'Diagnostic Analyses', icon: faSearch },
      { key: 'optimizations', label: 'Optimizations', icon: faBolt },
      { key: 'uploadCreatives', label: 'Upload Creatives', icon: faUpload },
      { key: 'assignCreatives', label: 'Assign Creatives', icon: faCheckCircle },
      { key: 'importCampaigns', label: 'Import Campaigns', icon: faDownload },
    ];

    // Render a detail table based on metric key
    const renderDetailTable = (metricKey) => {
      const data = metrics?.[metricKey];
      if (!data?.details || data.details.length === 0) {
        return <p className="no-details">No records found for this period.</p>;
      }

      const rows = data.details;

      // Determine columns based on metric type
      if (metricKey === 'campaignLaunches') {
        return (
          <table className="activity-table">
            <thead>
              <tr><th>Platform</th><th>Status</th><th>User</th><th>Date</th></tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td>{r.platform || '-'}</td>
                  <td><span className={`status-badge ${r.status}`}>{r.status}</span></td>
                  <td>{r.userName || r.userEmail || '-'}</td>
                  <td title={formatLocalTime(r.createdAt)}>{formatRelativeTime(r.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        );
      }

      if (metricKey === 'manualPacings') {
        return (
          <table className="activity-table">
            <thead>
              <tr><th>Name</th><th>Platform</th><th>Status</th><th>Campaigns (OK/Fail/Total)</th><th>User</th><th>Date</th></tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td>{r.name || '-'}</td>
                  <td>{r.platform || '-'}</td>
                  <td><span className={`status-badge ${r.status}`}>{r.status}</span></td>
                  <td>{r.successfulCampaigns}/{r.failedCampaigns}/{r.totalCampaigns}</td>
                  <td>{r.userName || r.userEmail || '-'}</td>
                  <td title={formatLocalTime(r.createdAt)}>{formatRelativeTime(r.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        );
      }

      if (metricKey === 'autoPacings') {
        return (
          <table className="activity-table">
            <thead>
              <tr><th>Name</th><th>Type</th><th>Platform</th><th>Status</th><th>Executions</th><th>User</th><th>Date</th></tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td>{r.name || '-'}</td>
                  <td>{r.type}</td>
                  <td>{r.platform || '-'}</td>
                  <td><span className={`status-badge ${r.status}`}>{r.status}</span></td>
                  <td>{r.type === 'per-campaign' ? `${r.successfulExecutions || 0}/${r.executions || 0}` : `${r.totalCampaigns || 0} campaigns`}</td>
                  <td>{r.userName || r.userEmail || '-'}</td>
                  <td title={formatLocalTime(r.createdAt)}>{formatRelativeTime(r.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        );
      }

      if (metricKey === 'diagnosticAnalyses' || metricKey === 'optimizations') {
        return (
          <table className="activity-table">
            <thead>
              <tr><th>Campaign</th><th>Platform</th><th>Status</th><th>Trigger</th><th>User</th><th>Date</th></tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td>{r.campaignId ? <Link to={`/campaigns/${r.campaignId}`} className="campaign-link">{r.campaignName || r.campaignId}</Link> : (r.campaignName || '-')}</td>
                  <td>{r.platform || '-'}</td>
                  <td><span className={`status-badge ${r.status}`}>{r.status}</span></td>
                  <td>{TRIGGER_LABEL[r.triggeredBy] || 'Manual'}</td>
                  {/* A machine run is recorded under a borrowed admin or a schedule owner; don't name them. */}
                  <td>{isMachineRun(r) ? 'Scheduler' : (r.userName || r.userEmail || '-')}</td>
                  <td title={formatLocalTime(r.createdAt)}>{formatRelativeTime(r.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        );
      }

      if (metricKey === 'uniqueCampaignsManaged') {
        return (
          <table className="activity-table">
            <thead>
              <tr><th>Campaign</th><th>Platform</th><th>Status</th><th>Last Updated</th></tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td>{r.campaignId ? <Link to={`/campaigns/${r.campaignId}`} className="campaign-link">{r.campaignName || r.campaignId}</Link> : (r.campaignName || '-')}</td>
                  <td>{r.platform || '-'}</td>
                  <td><span className={`status-badge ${r.status}`}>{r.status}</span></td>
                  <td title={formatLocalTime(r.updatedAt || r.createdAt)}>{formatRelativeTime(r.updatedAt || r.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        );
      }

      // Default table for campaignEdits, uploadCreatives, assignCreatives, importCampaigns.
      // optimizations now renders with diagnosticAnalyses above — it carries the
      // same cron rows and needs the Trigger column.
      return (
        <table className="activity-table">
          <thead>
            <tr><th>Campaign</th><th>Platform</th><th>Status</th><th>User</th><th>Date</th></tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td>{r.campaignId ? <Link to={`/campaigns/${r.campaignId}`} className="campaign-link">{r.campaignName || r.campaignId}</Link> : (r.campaignName || '-')}</td>
                <td>{r.platform || '-'}</td>
                <td><span className={`status-badge ${r.status}`}>{r.status}</span></td>
                <td>{r.userName || r.userEmail || '-'}</td>
                <td title={formatLocalTime(r.createdAt)}>{formatRelativeTime(r.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    };

    return (
      <div>
        {/* Controls row */}
        <div className="activity-report-controls">
          <div className="period-toggle">
            <button
              className={`period-toggle-btn ${activityPeriodType === 'month' ? 'active' : ''}`}
              onClick={() => handlePeriodTypeChange('month')}
            >
              Month
            </button>
            <button
              className={`period-toggle-btn ${activityPeriodType === 'week' ? 'active' : ''}`}
              onClick={() => handlePeriodTypeChange('week')}
            >
              Week
            </button>
          </div>

          <div className="period-selector">
            <input
              type={activityPeriodType}
              value={activityPeriodValue}
              onChange={(e) => setActivityPeriodValue(e.target.value)}
            />
          </div>

          {canDownloadReport && (
            <button
              className="download-csv-btn"
              onClick={handleDownloadExcel}
              disabled={activityLoading || !activityReport}
            >
              <FontAwesomeIcon icon={faDownload} />
              Download Report
            </button>
          )}
        </div>

        {/* Error state */}
        {activityError && (
          <div className="error-message">{activityError}</div>
        )}

        {/* Loading state */}
        {activityLoading && (
          <div className="activity-report-loading">Loading activity report...</div>
        )}

        {/* Metric cards - click to expand detail table */}
        {!activityLoading && (
          <>
            <div className="activity-metrics-grid">
              {metricCards.map((card) => (
                <div
                  key={card.key}
                  className={`activity-metric-card ${expandedMetric === card.key ? 'expanded' : ''}`}
                  onClick={() => setExpandedMetric(expandedMetric === card.key ? null : card.key)}
                  style={{ cursor: 'pointer' }}
                >
                  <FontAwesomeIcon icon={card.icon} className="metric-icon" />
                  <h4>{card.label}</h4>
                  <span className="metric-count">{metrics?.[card.key]?.count ?? '-'}</span>
                </div>
              ))}
            </div>

            {/* Expanded detail table */}
            {expandedMetric && metrics?.[expandedMetric] && (
              <div className="activity-detail-section">
                <h3>{metricCards.find(c => c.key === expandedMetric)?.label} — Details ({metrics[expandedMetric].count})</h3>
                {renderDetailTable(expandedMetric)}
              </div>
            )}
          </>
        )}
      </div>
    );
  }, [activityReport, activityLoading, activityError, activityPeriodType, activityPeriodValue, canDownloadReport, handleDownloadExcel, handlePeriodTypeChange, expandedMetric, formatLocalTime, formatRelativeTime]);

  // Helper function to render v2 campaign analytics view
  const renderV2Content = useCallback(() => {
    if (!v2Stats && activeTab === 'analytics') return null;

    const { overview, byOrigin, byStatus, topCreators, averagePerDay, recentCampaigns } = v2Stats || {};

    return (
      <div className="status-page-content">
        <h1 className="status-page-title">Campaign Analytics</h1>

        {/* Tab Navigation */}
        <div className="status-tabs">
          <button
            className={`status-tab ${activeTab === 'analytics' ? 'active' : ''}`}
            onClick={() => setActiveTab('analytics')}
          >
            <FontAwesomeIcon icon={faChartBar} className="tab-icon" />
            Campaign Analytics
          </button>
          <button
            className={`status-tab ${activeTab === 'activity-report' ? 'active' : ''}`}
            onClick={() => setActiveTab('activity-report')}
          >
            <FontAwesomeIcon icon={faClipboardList} className="tab-icon" />
            Activity Report
          </button>
        </div>

        {/* Activity Report Tab */}
        {activeTab === 'activity-report' && renderActivityReport()}

        {/* Analytics Tab - existing content */}
        {activeTab === 'analytics' && v2Stats && (
          <>
        {/* Overview Cards */}
        <div className="stats-grid v2-stats-grid">
          <div className="stat-card">
            <FontAwesomeIcon icon={faChartBar} className="stat-icon" />
            <h3>Total Campaigns</h3>
            <p className="stat-value">{overview?.totalCampaigns || 0}</p>
          </div>
          <div className="stat-card created">
            <FontAwesomeIcon icon={faUpload} className="stat-icon" />
            <h3>Created</h3>
            <p className="stat-value">{byOrigin?.created || 0}</p>
          </div>
          <div className="stat-card imported">
            <FontAwesomeIcon icon={faDownload} className="stat-icon" />
            <h3>Imported</h3>
            <p className="stat-value">{byOrigin?.imported || 0}</p>
          </div>
          <div className="stat-card active">
            <FontAwesomeIcon icon={faCheckCircle} className="stat-icon" />
            <h3>Active</h3>
            <p className="stat-value">{overview?.activeCampaigns || 0}</p>
          </div>
          <div className="stat-card deleted">
            <FontAwesomeIcon icon={faChartBar} className="stat-icon" />
            <h3>Deleted</h3>
            <p className="stat-value">{overview?.deletedCampaigns || 0}</p>
          </div>
          <div className="stat-card">
            <FontAwesomeIcon icon={faClock} className="stat-icon" />
            <h3>Last 7 Days</h3>
            <p className="stat-value">{overview?.campaignsLast7Days || 0}</p>
          </div>
        </div>

        {/* By Status Breakdown */}
        {byStatus && byStatus.length > 0 && (
          <div className="metrics-section">
            <h2>Campaigns by Status</h2>
            <div className="metrics-grid">
              {byStatus.map((item, index) => (
                <div key={index} className="metric-item">
                  <div className="metric-label">{item.status}</div>
                  <div className="metric-bar-container">
                    <div
                      className="metric-bar"
                      style={{
                        width: `${Math.min(100, (item.count / Math.max(...byStatus.map(i => i.count))) * 100)}%`
                      }}
                    ></div>
                    <span className="metric-value">{item.count}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Top Creators */}
        {topCreators && topCreators.length > 0 && (
          <div className="metrics-section">
            <h2>Top Creators</h2>
            <table className="activity-table creators-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Total</th>
                  <th>Created</th>
                  <th>Imported</th>
                </tr>
              </thead>
              <tbody>
                {topCreators.map((creator, index) => (
                  <tr key={index}>
                    <td>
                      <FontAwesomeIcon icon={faUsers} className="user-icon" />
                      {creator.displayName || creator.email}
                    </td>
                    <td>{creator.totalCampaigns}</td>
                    <td className="created-cell">{creator.createdCount}</td>
                    <td className="imported-cell">{creator.importedCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Average Per Day */}
        {averagePerDay && (
          <div className="metrics-section avg-section">
            <h2>Daily Averages (Last 30 Days)</h2>
            <div className="avg-grid">
              <div className="avg-card">
                <h4>Total</h4>
                <p className="avg-value">{averagePerDay.total}</p>
                <span>campaigns/day</span>
              </div>
              <div className="avg-card created">
                <h4>Created</h4>
                <p className="avg-value">{averagePerDay.created}</p>
                <span>campaigns/day</span>
              </div>
              <div className="avg-card imported">
                <h4>Imported</h4>
                <p className="avg-value">{averagePerDay.imported}</p>
                <span>campaigns/day</span>
              </div>
            </div>
          </div>
        )}

        {/* Recent 50 Campaigns */}
        {recentCampaigns && recentCampaigns.length > 0 && (
          <div className="recent-activity">
            <h2>Recent 50 Campaigns</h2>
            <table className="activity-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Platform</th>
                  <th>Status</th>
                  <th>Origin</th>
                  <th>Folder</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {recentCampaigns.map((campaign, index) => (
                  <tr key={index}>
                    <td>
                      <Link to={`/campaigns/${campaign.id}`} className="campaign-link">
                        {campaign.name}
                      </Link>
                    </td>
                    <td>{campaign.platform}</td>
                    <td>
                      <span className={`status-badge ${campaign.status}`}>
                        {campaign.status}
                      </span>
                    </td>
                    <td>
                      <span className={`origin-badge ${campaign.origin}`}>
                        {campaign.origin}
                      </span>
                    </td>
                    <td>{campaign.folderName || 'Uncategorized'}</td>
                    <td title={formatLocalTime(campaign.createdAt)}>
                      {formatRelativeTime(campaign.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
          </>
        )}
      </div>
    );
  }, [v2Stats, activeTab, renderActivityReport, formatLocalTime, formatRelativeTime]);

  // Memoize the renderContent function to prevent unnecessary re-creation
  const renderContent = useCallback(() => {
    if (isLoading) {
      return (
        <div className="status-page-content loading-container">
          <div className="loading-content">
            {/* <div className="loading-spinner"></div> */}
            <p>Loading statistics...</p>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="status-page-content loading-container">
          <div className="loading-content">
            <p>{error}</p>
            <button className="retry-button" onClick={() => window.location.reload()}>Retry</button>
          </div>
        </div>
      );
    }

    // V2 users see campaign analytics (no orchestration section - that's for v1 only)
    if (isV2User) {
      return renderV2Content();
    }

    // V1 users see session statistics
    return (
      <div className="status-page-content">
        <h1 className="status-page-title">Session Statistics</h1>

        <div className="stats-grid">
          <div className="stat-card">
            <FontAwesomeIcon icon={faChartBar} className="stat-icon" />
            <h3>Total Chats</h3>
            <p className="stat-value">{stats?.totalChats || 0}</p>
          </div>
          <div className="stat-card">
            <FontAwesomeIcon icon={faMessage} className="stat-icon" />
            <h3>Total Messages</h3>
            <p className="stat-value">{stats?.totalMessages || 0}</p>
          </div>
          <div className="stat-card">
            <FontAwesomeIcon icon={faFile} className="stat-icon" />
            <h3>Files Uploaded</h3>
            <p className="stat-value">{stats?.filesUploaded || 0}</p>
          </div>
        </div>

        {stats?.messagesPerAction && stats.messagesPerAction.length > 0 && (
          <div className="metrics-section">
            <h2>Messages Per Action</h2>
            <div className="metrics-grid">
              {stats.messagesPerAction.map((item, index) => (
                <div key={index} className="metric-item">
                  <div className="metric-label">{item.action}</div>
                  <div className="metric-bar-container">
                    <div
                      className="metric-bar"
                      style={{
                        width: `${Math.min(100, (item.count / Math.max(...stats.messagesPerAction.map(i => i.count))) * 100)}%`
                      }}
                    ></div>
                    <span className="metric-value">{item.count}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {stats?.recentActivity && stats.recentActivity.length > 0 && (
          <div className="recent-activity">
            <h2>Recent Activity</h2>
            <table className="activity-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Chat</th>
                  <th>User</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {stats.recentActivity.map((activity, index) => (
                  <tr key={index}>
                    <td title={formatLocalTime(activity.timestamp)}>
                      {formatRelativeTime(activity.timestamp)}
                    </td>
                    <td>{activity.chatSummary}</td>
                    <td>{activity.userName || 'You'}</td>
                    <td>{activity.action}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Orchestration Status Section */}
        {renderOrchestrationSection()}
      </div>
    );
  }, [isLoading, error, stats, isV2User, renderV2Content, renderOrchestrationSection, formatLocalTime, formatRelativeTime]);

  return renderContent();
}

// Export the component wrapped with the account selection HOC
export default withAccountSelection(StatusPage);
